import React, { useState, useEffect, useMemo } from 'react';
import {
  DollarSign,
  Search,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Printer,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  FileText
} from 'lucide-react';
import { useAreaCliente } from '../../contexts/AreaClienteContext';
import { areaClienteService } from '../../services/areaClienteService';
import { Conta } from '../../types/financeiro';

export function AreaClienteFinanceiro() {
  const { clienteAtual } = useAreaCliente();
  const [contas, setContas] = useState<Conta[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('todos');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const loadFinanceiro = async () => {
    if (!clienteAtual) return;
    setIsLoading(true);
    try {
      const data = await areaClienteService.getExtratoFinanceiroCliente(clienteAtual.id);
      setContas(data);
    } catch (err) {
      console.error('Erro ao carregar extrato financeiro do cliente:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFinanceiro();
  }, [clienteAtual]);

  const hoje = new Date().toISOString().split('T')[0];

  const formatCurrency = (val?: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(val || 0);
  };

  // Summaries
  const totals = useMemo(() => {
    let pendente = 0;
    let pago = 0;
    let vencido = 0;
    let geral = 0;

    contas.forEach(c => {
      const val = Number(c.valor || 0);
      geral += val;
      if (c.dt_pagamento) {
        pago += val;
      } else {
        if (c.dt_vencimento && c.dt_vencimento < hoje) {
          vencido += val;
        } else {
          pendente += val;
        }
      }
    });

    return { pendente, pago, vencido, geral };
  }, [contas, hoje]);

  // Filtered List
  const filteredContas = useMemo(() => {
    return contas.filter(c => {
      const isPaid = !!c.dt_pagamento;
      const isOverdue = !isPaid && c.dt_vencimento && c.dt_vencimento < hoje;
      const isOpen = !isPaid && !isOverdue;

      if (statusFilter === 'aberto' && !isOpen) return false;
      if (statusFilter === 'pago' && !isPaid) return false;
      if (statusFilter === 'vencido' && !isOverdue) return false;

      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const matchDesc = (c.descricao || '').toLowerCase().includes(term);
        const matchId = String(c.id).includes(term);
        const matchObs = (c.obs || '').toLowerCase().includes(term);
        if (!matchDesc && !matchId && !matchObs) return false;
      }

      if (startDate && c.dt_vencimento && c.dt_vencimento < startDate) return false;
      if (endDate && c.dt_vencimento && c.dt_vencimento > endDate) return false;

      return true;
    });
  }, [contas, statusFilter, searchTerm, startDate, endDate, hoje]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl">
            <DollarSign className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-slate-900">Extrato Financeiro</h1>
            <p className="text-xs text-slate-500">
              Acompanhe suas faturas, títulos em aberto, vencimentos e histórico de pagamentos.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            className="px-3.5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors text-xs font-semibold flex items-center gap-1.5"
            title="Imprimir Extrato"
          >
            <Printer className="w-4 h-4" />
            <span>Imprimir</span>
          </button>
          <button
            onClick={loadFinanceiro}
            className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors text-xs font-semibold flex items-center gap-1.5"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Atualizar</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* A Vencer / Em Aberto */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">A Vencer (Em Aberto)</span>
            <Clock className="w-4 h-4 text-amber-500" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-black text-amber-700">{formatCurrency(totals.pendente)}</span>
            <p className="text-[11px] text-slate-400 mt-1">Aguardando vencimento</p>
          </div>
        </div>

        {/* Vencidos */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-red-600">Vencidos</span>
            <AlertTriangle className="w-4 h-4 text-red-500" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-black text-red-700">{formatCurrency(totals.vencido)}</span>
            <p className="text-[11px] text-red-500 mt-1 font-medium">Necessita regularização</p>
          </div>
        </div>

        {/* Pagos */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600">Total Quitado</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-black text-emerald-700">{formatCurrency(totals.pago)}</span>
            <p className="text-[11px] text-emerald-600 mt-1 font-medium">Pagamentos confirmados</p>
          </div>
        </div>

        {/* Total Geral */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Volume Total</span>
            <DollarSign className="w-4 h-4 text-slate-400" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-black text-slate-900">{formatCurrency(totals.geral)}</span>
            <p className="text-[11px] text-slate-400 mt-1">{contas.length} títulos emitidos</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'todos', label: 'Todos os Lançamentos', count: contas.length },
            { id: 'aberto', label: 'Em Aberto', count: contas.filter(c => !c.dt_pagamento && (!c.dt_vencimento || c.dt_vencimento >= hoje)).length },
            { id: 'vencido', label: 'Vencidos', count: contas.filter(c => !c.dt_pagamento && c.dt_vencimento && c.dt_vencimento < hoje).length },
            { id: 'pago', label: 'Quitados', count: contas.filter(c => !!c.dt_pagamento).length }
          ].map(tab => {
            const isActive = statusFilter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setStatusFilter(tab.id)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center space-x-1.5 ${
                  isActive
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <span>{tab.label}</span>
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                  isActive ? 'bg-slate-800 text-slate-200' : 'bg-slate-200 text-slate-700'
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por descrição ou número de título..."
              className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              title="Vencimento a partir de"
            />
          </div>

          <div>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              title="Vencimento até"
            />
          </div>
        </div>
      </div>

      {/* Financial Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center">
            <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-3 text-xs text-slate-500">Carregando extrato financeiro...</p>
          </div>
        ) : filteredContas.length === 0 ? (
          <div className="py-16 text-center space-y-3 px-4">
            <DollarSign className="w-12 h-12 text-slate-300 mx-auto" />
            <h3 className="text-sm font-bold text-slate-800">Nenhum título encontrado</h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              Nenhuma fatura ou lançamento corresponde aos filtros selecionados.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="py-3 px-4">ID</th>
                  <th className="py-3 px-4">Descrição / Referência</th>
                  <th className="py-3 px-4">Vencimento</th>
                  <th className="py-3 px-4">Pagamento</th>
                  <th className="py-3 px-4">Valor (R$)</th>
                  <th className="py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredContas.map((conta) => {
                  const isPaid = !!conta.dt_pagamento;
                  const isOverdue = !isPaid && conta.dt_vencimento && conta.dt_vencimento < hoje;

                  return (
                    <tr key={conta.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3.5 px-4 font-black text-slate-900">
                        #{conta.id}
                      </td>
                      <td className="py-3.5 px-4 max-w-[280px]">
                        <p className="font-semibold text-slate-800 truncate" title={conta.descricao}>
                          {conta.descricao || 'Fatura de Serviços de Transporte'}
                        </p>
                        {conta.obs && (
                          <p className="text-[10px] text-slate-400 truncate mt-0.5">{conta.obs}</p>
                        )}
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap font-medium text-slate-700">
                        {conta.dt_vencimento ? new Date(conta.dt_vencimento + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap text-slate-500">
                        {conta.dt_pagamento ? (
                          <span className="text-emerald-700 font-semibold">
                            {new Date(conta.dt_pagamento + 'T00:00:00').toLocaleDateString('pt-BR')}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3.5 px-4 font-black text-slate-900 whitespace-nowrap">
                        {formatCurrency(conta.valor)}
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        {isPaid ? (
                          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1 w-fit">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Quitado
                          </span>
                        ) : isOverdue ? (
                          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-300 flex items-center gap-1 w-fit">
                            <AlertTriangle className="w-3 h-3 text-red-600" /> Vencido
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1 w-fit">
                            <Clock className="w-3 h-3 text-amber-600" /> Em Aberto
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
